import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Intro to Pliable Concepts',
  content: `
Pliable is a low-code platform that turns raw data into governed, queryable analytics. This guide walks through the core concepts end-to-end: from connecting your data all the way to consuming insights in dashboards or through AI-powered conversation.

# The Big Picture

At a high level, data flows through Pliable in five stages:

1. **Connect** — Bring data into Pliable from Snowflake tables, CSV uploads, or third-party connectors.
2. **Clean** — Apply column formatting, filtering, and grouping rules to prepare your raw data.
3. **Model** — Define business concepts as entities, connect them with relationships, and create metrics.
4. **Map** — Link your cleaned source data to the entities you've defined.
5. **Consume** — Explore your data through charts, dashboards, or natural language conversations with Ellie, Pliable's AI analyst.

Each stage builds on the one before it, but you can revisit and refine any stage at any time.

# Data Sources

Everything starts with a **data source**. A data source is any external data repository — a Snowflake table, a CSV file, or data pulled in through a third-party connector. When you connect a data source, Pliable imports the raw data and creates a **source node** that represents it in your pipeline.

For more details, see [Data Sources](/docs/data-sources) and [Connecting to Snowflake](/docs/connecting-to-snowflake).

# Nodes and Data Cleaning

A **node** is a step in your data pipeline. Source nodes represent raw data as it was imported. From there, you can apply **cleaning rules** to transform, filter, and reshape your data before it reaches the semantic layer.

Cleaning includes:

- **[Column Formatting](/docs/column-formatting)** — Rename columns, change data types, apply transformations
- **[Filtering](/docs/filtering)** — Include or exclude rows based on rules or custom SQL
- **[Grouping](/docs/grouping-behaviors)** — Aggregate or deduplicate records

Think of nodes as a visual, step-by-step pipeline: raw data enters on the left, and clean, structured data exits on the right, ready to be mapped to your semantic layer.

# The Semantic Layer

The semantic layer is the heart of Pliable. It's where you define what your data *means* in business terms, independent of where it comes from or how it's stored. The semantic layer has three building blocks:

## Entities

An **entity** represents a core business concept — Customers, Orders, Products, Campaigns, etc. Each entity has **columns** that describe its attributes (e.g., a Customer has a Name, Email, and Signup Date). You can flag columns as **dimensions** to make them available for grouping and slicing in reports.

See [Entities](/docs/entities) for details.

## Relationships

**Relationships** connect entities to each other, reflecting the natural associations in your business. For example, an Order relates to a Customer and to a Product. Defining relationships unlocks multi-dimensional reporting: you can analyze orders broken down by customer region, product category, or any other dimension across connected entities.

See [Relationships](/docs/relationships) for details.

## Metrics

**Metrics** are calculated values derived from entity data — Total Revenue (SUM of Order Amount), Customer Count (COUNT DISTINCT of Customer Email), Average Order Value, and so on. Metrics can be **simple** (an aggregation on a single entity column) or **complex** (a formula combining other metrics, like Revenue per Customer).

See [Metrics](/docs/metrics) for details.

# Data Mapping

**Data mapping** is the bridge between your cleaned source data and the semantic layer. You map columns from your source nodes to the attributes of your entities — for example, mapping the \`cust_email\` column in your raw orders table to the \`Email\` attribute on your Customer entity.

Pliable includes an **AI mapping** feature that can automatically suggest the best column matches, or you can drag and drop manually.

See [Data Mapping](/docs/data-mapping) for details.

# Running a Build

After connecting sources, cleaning data, defining your semantic layer, and mapping everything together, you run a **build**. A build executes the full pipeline — extracting data from sources, applying cleaning rules, populating entities, computing metrics, and materializing the results so they're ready to query.

See [Running Builds](/docs/running-builds) for details.

# Consuming Your Data

Once your data is built, there are three main ways to consume it:

## Charts and Dashboards

Pliable's built-in **report builder** lets you create charts and dashboards directly from your semantic layer. Pick entities, dimensions, and metrics, and Pliable generates visualizations that are always grounded in your governed definitions. Dashboards can combine multiple charts into a single view for stakeholders.

See [Charts](/docs/charts) and [Dashboards](/docs/dashboards) for details.

## Ask AI (Ellie)

**Ellie** is Pliable's AI analyst. Instead of building charts manually, you can ask questions in plain English — "What was last quarter's revenue by region?" — and Ellie generates the answer using your semantic layer. Because Ellie queries the same governed entities and metrics, everyone gets consistent numbers. You can refine results through conversation and save the output to dashboards.

See [Ask AI](/docs/ask-ai) for details.

## Pliable API

You can also push data out of Pliable via the **Pliable API**. This is useful for feeding governed data into downstream tools, BI platforms, or applications.

See [Pliable API](/docs/pliable-api) for details.

# Putting It All Together

Here's a typical workflow:

1. Connect your Snowflake tables as data sources
2. Clean the raw data — fix column types, filter out test records, deduplicate
3. Define entities (Customers, Orders, Products) and their attributes in the semantic layer
4. Create relationships (Orders → Customers, Orders → Products)
5. Define metrics (Total Revenue, Customer Count, Average Order Value)
6. Map your cleaned source columns to entity attributes
7. Run a build
8. Create dashboards or ask Ellie questions to start exploring your data

Each of these steps is covered in more detail in the pages linked throughout this guide.
`,
};

export default doc;
